<template>
  <div class="details">
    <div class="details__title px-5"></div>

    <div class="details__content container">
      <h1 class="title is-3">{{ $t("flights.title") }}</h1>
      <div class="columns is-multiline details__content__box m-0 mb-5">
        <div
          class="field column is-one-third-desktop"
          v-if="
            companyInfo.userSettings?.canBookOnBehalfOfOthers &&
            companyInfo.allowListedEmployees &&
            businessUnits
          "
        >
          <h5 class="title is-5">{{ $t("ReservationForm.findEmployee") }}</h5>

          <EmployeeSelection
            :is-flights="true"
            :pre-selected-employees="guests"
            :business-units="businessUnits"
            :selected-employees="selectedEmployees"
            @selected-employee="addEmployee"
          />
          <p v-if="companyInfo.allowListedEmployees" class="mt-3">
            {{ $t("ReservationForm.unknownEmployee") }}
          </p>
        </div>

        <div
          class="field column is-one-third-desktop"
          v-if="
            !companyInfo?.userSettings?.canBookOnBehalfOfOthers ||
            (!companyInfo.allowUnlistedEmployees &&
              !companyInfo.allowListedEmployees)
          "
        >
          <h5 class="title is-5">
            {{ $t("ReservationForm.personalReservationHeader") }}
          </h5>

          <p>{{ $t("ReservationForm.personalReservation") }}</p>
        </div>

        <div
          class="field column is-one-third-desktop"
          v-if="
            companyInfo.userSettings?.canBookOnBehalfOfOthers &&
            companyInfo.allowUnlistedEmployees
          "
        >
          <AddUnlistedEmployee
            :is-flights="true"
            @created-unlisted-employee="addUnlistedEmployee"
          />
        </div>

        <div class="field column is-one-third-desktop">
          <template v-if="companyInfo.employeeIsRequired">
            <h5 class="title is-5">
              {{ $t("ReservationForm.selectedPassengers") }}
            </h5>

            <br />
            <i
              v-if="
                selectedEmployees.length == 0 &&
                selectedUnlistedEmployees.length == 0
              "
            >
              {{ $t("ReservationForm.noPassengersAdded") }}</i
            >
            <div
              class="is-flex is-align-items-center is-justify-content-space-between mb-2"
              v-for="e in selectedEmployees"
              :key="e"
            >
              <span class="u-weight--bold"
                >{{ e.firstName }} {{ e.lastName }}</span
              >
              <button
                class="c-button c-button--small is-danger"
                @click.stop.prevent="removeSelectedEmployee(e)"
              >
                <span class="icon is-small is-danger">
                  <i class="fas fa-times"></i>
                </span>
              </button>
            </div>
            <br />
            <br />
          </template>
          <div
            class="is-flex is-align-items-center is-justify-content-space-between mb-2"
            v-for="e in selectedUnlistedEmployees"
            :key="e"
          >
            <strong
              >{{ e.firstName }} {{ e.lastName }} ({{ e.emailAddress }})</strong
            >
            <button class="c-button c-button--small is-danger">
              <span
                class="icon is-small is-danger"
                @click.stop.prevent="removeUnlistedEmployees(e)"
              >
                <i class="fas fa-times"></i>
              </span>
            </button>
            <br />
            <br />
          </div>
          <SelectedBusinessUnit
            :business-units="businessUnits"
            :selected-business-unit="selectedBusinessUnit"
            @selected-business-unit="selectBusinessUnit"
            @remove-business-unit="removeBusinessUnit"
          />
        </div>
      </div>
      <div class="columns is-full-width details__content__box m-0 mb-5">
        <div
          v-for="type in roundTripTypes"
          class="column is-one-quarter"
          :key="type"
        >
          <label>
            <input
              type="radio"
              :value="type.value"
              v-model="selectedRoundTripType"
            />
            {{ type.label }}
          </label>
        </div>
      </div>
      <FlightLegs
        @legs-selected="selectedLegs"
        :selectedRoundtripType="selectedRoundTripType"
      />
      <form @submit.prevent="makeReservation()">
        <div class="columns is-multiline details__content__box m-0 mb-5">
          <div class="field column is-half-desktop">
            <label for="notes">{{ $t("ReservationForm.notes") }} </label>
            <div class="control">
              <textarea
                class="textarea"
                v-model="notes"
                id="notes"
                name="notes"
                type="text"
                rows="5"
                :placeholder="$t('ReservationForm.flightNotesPlaceholder')"
              ></textarea>
            </div>
            <CustomField
              v-for="field in companyInfo.customFields"
              :key="field.name"
              :customField="field"
              @setted-value="setValue"
            ></CustomField>
          </div>
          <div class="field column is-half-desktop">
            <h6 class="title is-6">{{ $t("ReservationForm.flightClass") }}</h6>

            <br />
            <div v-for="flightClass in flightClasses" :key="flightClass.value">
              <label>
                <input
                  type="radio"
                  :value="flightClass.value"
                  v-model="selectedFlightClass"
                />
                {{ flightClass.label }}
              </label>
            </div>

            <br />
            <label class="checkbox">
              <input type="checkbox" v-model="selectedHoldLuggage" />

              {{ $t("ReservationForm.checkedLuggage") }}
            </label>
            <br />
            <label class="checkbox">
              <input type="checkbox" v-model="changesAllowed" />

              {{ $t("ReservationForm.changesAllowed") }}
            </label>

            <div class="field">
              <label class="checkbox">
                <input type="checkbox" v-model="carRentalSelected" />
                {{ $t("CarReservationForm.CarRental") }}
              </label>
            </div>

            <CarReservationModule
              v-if="carRentalSelected"
              :available-drivers="availableDrivers"
              @car-data-changed="handleCarDataChanged"
              :carReturnDateUnknown="selectedRoundTripType === 'oneWay'"
              :minCarReturnDate="this.legs[0]?.departureDate ?? new Date()"
            />
            <br /><br />
            <button
              class="c-button c-button--primary"
              :disabled="disableButton"
              type="submit"
            >
              {{ $t("sendRequest") }}
            </button>
          </div>
        </div>
      </form>
    </div>
    <NotificationMessage
      :level="notification.level"
      :autohide="false"
      :title="notification.title"
      :message="notification.message"
      :show="notification.show"
      @toggle="toggleNotification"
    />
  </div>
</template>
<script>
import CommonHelper from "@/helpers/common.helper.js";

import SelectedBusinessUnit from "./components/reservationform/SelectedBusinessUnit.vue";
import EmployeeSelection from "./components/reservationform/EmployeeSelection.vue";
import AddUnlistedEmployee from "./components/reservationform/AddUnlistedEmployee.vue";
import CustomField from "./components/reservationform/CustomField.vue";
import FlightLegs from "./components/reservationform/FlightLegs.vue";
import CarReservationModule from "./components/reservationform/CarReservationModule.vue";

export default {
  components: {
    // eslint-disable-next-line
    FlightLegs,
    SelectedBusinessUnit,
    EmployeeSelection,
    AddUnlistedEmployee,
    CustomField,
    CarReservationModule
  },
  data() {
    return {
      selectedFlightClass: "economy",
      selectedHoldLuggage: false,
      changesAllowed: true,
      isLoading: false,
      legs: [],
      selectedRoundTripType: "roundTrip",
      isPricesLoading: false,
      disableButton: false,
      notes: "",
      guests: "",
      showEditableFields: false,
      returnedData: null,
      businessUnits: null,
      allowBusinessUnitClearing: false,
      selectedEmployees: [],
      selectedUnlistedEmployees: [],
      selectedBusinessUnit: null,
      errorMessages: [],
      companyInfo: {},
      reservationMeta: {
        businessUnit: null,
        location: null,
        dateRange: {
          startDate: null,
          endDate: null,
          startTime: null,
          endTime: null
        }
      },
      carRentalSelected: false,
      availableDrivers: [],
      carData: {
        selectedCarClass: "",
        selectedCarTransmissionType: "",
        driver: {},
        carReturnDate: "",
        carReturnDayPart: ""
      },
      customFieldFormData: {},
      isSaving: false,
      roundTripTypes: [
        { value: "oneWay", label: this.$t("ReservationForm.oneWay") },
        { value: "roundTrip", label: this.$t("ReservationForm.roundTrip") },
        { value: "multiLeg", label: this.$t("ReservationForm.multiLeg") }
      ],
      flightClasses: [
        { value: "economy", label: this.$t("ReservationForm.economy") },
        {
          value: "PREMIUM_ECONOMY",
          label: this.$t("ReservationForm.PREMIUM_ECONOMY")
        },
        { value: "business", label: this.$t("ReservationForm.business") }
      ]
    };
  },
  async created() {
    this.getCompanyInfo();

    let companyInfo = await CommonHelper.getCompanyInfo();
    if (companyInfo && companyInfo.budget) {
      this.budget = companyInfo.budget;
    }

    this.loadDetails();
  },

  computed: {
    numberOfPersons() {
      const selectedNumber =
        this.selectedEmployees.length + this.selectedUnlistedEmployees.length;
      return selectedNumber;
    }
  },
  methods: {
    loadDetails() {},
    selectedLegs(legs) {
      this.legs = legs;
    },
    setValue(data) {
      this.customFieldFormData[data.field] = data.value;
    },
    async getCompanyInfo() {
      this.companyInfo = await CommonHelper.getCompanyInfo();

      this.companyInfo.employeeIsRequired =
        this.companyInfo.userSettings.canBookOnBehalfOfOthers &&
        (this.companyInfo.allowUnlistedEmployees ||
          this.companyInfo.allowListedEmployees);

      this.companyInfo.customFields.sort((a, b) => {
        return Number(a.mandatory) > Number(b.mandatory) ? -1 : 1;
      });

      this.businessUnits = await CommonHelper.getBusinessUnits();

      // Initialize available drivers with selected employees
      this.updateAvailableDrivers();
    },
    updateAvailableDrivers() {
      this.availableDrivers = [
        ...this.selectedEmployees,
        ...this.selectedUnlistedEmployees
      ];
    },
    selectBusinessUnit(data) {
      this.selectedBusinessUnit = data.businessUnit;
    },
    removeBusinessUnit() {
      this.selectedBusinessUnit = null;
    },
    addEmployee(data) {
      const employeeAlreadySelected = this.selectedEmployees.find(
        (sE) => sE.id == data.id
      );

      if (!employeeAlreadySelected) {
        this.selectedEmployees.push(data.employee);

        if (this.selectedBusinessUnit == null && data.businessUnit) {
          this.selectBusinessUnit(data);
        }

        this.updateAvailableDrivers();
      }
    },
    addEmployeeDriver(data) {
      // This method is kept for compatibility but the CarReservationModule handles driver selection internally
      if (data?.employee) {
        this.availableDrivers = [data.employee];
      }
    },
    handleCarDataChanged(data) {
      this.carData = data;
    },
    removeSelectedDriver() {
      this.carData.driver = {};
    },
    removeSelectedEmployee(e) {
      this.selectedEmployees = this.selectedEmployees.filter(
        (se) => se.id != e.id
      );
      this.updateAvailableDrivers();
    },
    addUnlistedEmployee(data) {
      this.selectedUnlistedEmployees.push(data.employee);
      this.updateAvailableDrivers();
    },
    removeUnlistedEmployees(e) {
      this.selectedUnlistedEmployees = this.selectedUnlistedEmployees.filter(
        (uE) => uE.id != e.id
      );
      this.updateAvailableDrivers();
    },
    async makeReservation() {
      // eslint-disable-next-line no-undef
      let url = `${this.AVAILABILITY_API_URL}/reservation/flight`;
      let saveObject = {};
      saveObject.legs = this.legs;
      let dataIsValid = this.validateForm();

      if (dataIsValid) {
        this.isSaving = true;
        let notes = this.notes;
        const employees = this.selectedEmployees.map((e) => e.id);
        const unlistedEmployees = this.selectedUnlistedEmployees.map((e) => {
          return {
            firstName: e.firstName,
            lastName: e.lastName,
            emailAddress: e.emailAddress
          };
        });
        const businessUnitId = this.selectedBusinessUnit
          ? this.selectedBusinessUnit.id
          : null;

        let reservationList = [
          {
            customFields: this.customFieldFormData,
            changesAllowed: this.changesAllowed,
            travelClass: this.selectedFlightClass,
            holdLuggage: this.selectedHoldLuggage,
            businessUnitId: businessUnitId,
            flights: this.legs,
            notes: notes,
            employeeIds: employees,
            guests: unlistedEmployees,
            travellers: unlistedEmployees,
            vehicleType: 0
          }
        ];

        if (this.carRentalSelected) {
          const rentalObj = {
            carClass: this.carData.selectedCarClass,
            transmissionType: this.carData.selectedCarTransmissionType,
            departureLocation: this.legs[0].departureAirport?.city || "",
            destinationLocation:
              this.legs[this.legs.length - 1].arrivalAirport?.city || "",
            departureDate: this.legs[0].departureDate,
            departureTime: null,
            returnDate: null,
            returnTime: null,
            customFields: this.customFieldFormData,
            notes: notes,
            businessUnitId: businessUnitId,
            vehicleType: "RENTAL_CAR",
            driver: this.carData.driver
          };

          if (this.legs.length > 1) {
            rentalObj.returnDate = this.legs[1].departureDate;
          } else if (this.legs.length === 1 && this.carData.carReturnDate) {
            rentalObj.returnDate = this.carData.carReturnDate;
          } else if (this.legs.length === 1) {
            rentalObj.returnDate = this.legs[0].departureDate;
          }

          reservationList.push(rentalObj);
        }

        let saveData = {
          reservationList: reservationList
        };

        Object.keys(this.customFieldFormData).forEach((key) => {
          saveData.reservationList[0].customFields[key] =
            this.customFieldFormData[key];
        });

        this.saveData = JSON.stringify(saveData, null, " ");

        this.disableButton = true;
        const { status, data } = await this.axios.post(url, saveData);

        if (status == 200) {
          this.notification.title = this.$t("ReservationForm.savedTitle");
          this.notification.message = this.$t("ReservationForm.savedMessage");
          this.notification.show = true;
          this.notification.level = "success";
          setTimeout(() => {
            this.notification.show = false;
            this.disableButton = false;
          }, 30 * 1000);
          let unlistedEmployeesCount = this.selectedUnlistedEmployees.length;
          let listedEmployeesCount = this.selectedEmployees.length;
          let employeeCount = unlistedEmployeesCount + listedEmployeesCount;
          let trackData = {
            reservationType: "flight",
            departureAirports: this.legs
              .map((l) => l.departureAirport.city)
              .join(", "),
            arrivalAirports: this.legs
              .map((l) => l.arrivalAirport.city)
              .join(", "),
            legs: this.legs.length,
            numberOfPersons: employeeCount,
            status: "success"
          };
          this.track("flightReservation", trackData);
          this.$router.push("/flight-confirmation");
        } else {
          this.notification.title = this.$t("ReservationForm.errorTitle");
          this.notification.message = this.$t("ReservationForm.errorMessage");
          this.notification.show = true;
          this.notification.level = "danger";
          this.disableButton = false;
          setTimeout(() => {
            this.notification.show = false;
          }, 30 * 1000);
        }
      }

      this.isSaving = false;
    },
    validateForm() {
      this.errorMessages = [];
      this.showNotification = false;

      const businessUnitMandatory = this.companyInfo.businessUnitMandatory;
      const businessUnitIsSelected = !!this.selectedBusinessUnit;

      const employeeIsRequired = this.companyInfo.employeeIsRequired;

      let employeeIsSelected =
        this.selectedUnlistedEmployees.length > 0 ||
        this.selectedEmployees.length > 0;
      let isValid = true;

      if (businessUnitMandatory && !businessUnitIsSelected) {
        isValid = false;
        let message = this.$t("error.businessunitMandatory");
        message = this.tbu(message);
        this.errorMessages.push(message);
      }
      if (employeeIsRequired && !employeeIsSelected) {
        isValid = false;
        const message = this.$t("error.employeeMandatory");
        this.errorMessages.push(message);
      }

      if (this.legs.length < 1) {
        isValid = false;
        const message = this.$t("error.noLegs");
        this.errorMessages.push(message);
      }

      let customFieldsValid = true;
      let invalidCustomFields = [];
      this.companyInfo.customFields.forEach((field) => {
        if (field.mandatory) {
          let thisFieldHasError = false;
          if (field.type === "checkbox") {
            if (
              !this.customFieldFormData[field.name] ||
              this.customFieldFormData[field.name].length < 1
            ) {
              thisFieldHasError = true;
            }
          } else {
            if (
              !this.customFieldFormData[field.name] ||
              this.customFieldFormData[field.name] == ""
            ) {
              thisFieldHasError = true;
            }
          }
          if (thisFieldHasError) {
            customFieldsValid = false;
            isValid = false;
            invalidCustomFields.push(this.getTitle(field));
          }
        }
      });

      if (!customFieldsValid) {
        let message = this.$t("error.customFields");
        message += ` (${invalidCustomFields.join(", ")})`;
        this.errorMessages.push(message);
      }

      if (!isValid) {
        let notificationMessage = `${this.$t("error.genericIntro")} <ul>
        <li>- ${this.errorMessages.join("</li><li>- ")}</li></ul>`;

        this.track("hotelReservationErrors", {
          error: this.errorMessages.join(", ")
        });

        this.notification.title = this.$t("error.genericTitle");
        this.notification.message = notificationMessage;
        this.notification.level = "danger";
        this.notification.show = true;

        setTimeout(() => {
          this.notification.show = false;
        }, 10 * 1000);
        return false;
      } else {
        return true;
      }
    },
    getTitle(field) {
      return this.getFieldNameTranslation(field);
    }
  }
};
</script>
<style scoped>
.details__title {
  background: url("@/assets/images/flight-bg.webp") center center no-repeat;
  background-size: cover;
}
</style>
